export type InviteKind = 'management' | 'staff' | 'owner'

export type InviteAvailability =
  | 'available'
  | 'expired'
  | 'exhausted'
  | 'revoked'
  | 'not_found'

export type InviteAccountAccess =
  | 'target_active'
  | 'other_active'
  | 'none'
  | 'target_revoked'
  | 'disabled'

export interface InviteEntryContext {
  kind: InviteKind
  authenticated: boolean
  availability: InviteAvailability
  accountAccess: InviteAccountAccess
  identityConflict?: boolean
}

export type InviteEntryDecision =
  | {
      action: 'open_account'
      destination: '/'
      reason: 'target_access_exists'
    }
  | {
      action: 'authenticate'
      mode: 'accept_invite'
      preserveInvite: true
    }
  | {
      action: 'show_invite'
      kind: InviteKind
    }
  | {
      action: 'show_terminal'
      reason: InviteAvailability | 'access_revoked' | 'identity_conflict'
    }
  | {
      action: 'show_access_disabled'
    }

export interface RawInviteAvailability {
  exists: boolean
  isActive?: boolean | null
  expiresAt?: string | null
  currentUses?: number | null
  maxUses?: number | null
  legacyUsed?: boolean | null
  /**
   * Audit timestamp only. It must not exhaust a multi-use invite by itself.
   */
  usedAt?: string | null
}

export function getInviteTokenFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/invite\/([^/]+)$/)
  if (!match?.[1]) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

export function resolveInviteAvailability(
  invite: RawInviteAvailability,
  now = new Date()
): InviteAvailability {
  if (!invite.exists) return 'not_found'
  if (invite.isActive === false) return 'revoked'

  if (invite.expiresAt) {
    const expiresAt = Date.parse(invite.expiresAt)
    if (!Number.isNaN(expiresAt) && expiresAt <= now.getTime()) {
      return 'expired'
    }
  }

  const maxUses = invite.maxUses ?? 1
  const currentUses = invite.currentUses ?? 0
  if (invite.legacyUsed === true || currentUses >= maxUses) {
    return 'exhausted'
  }

  return 'available'
}

/**
 * Pure policy for opening an invite link. It deliberately has no storage,
 * routing or API side effects, so UI and server adapters can share one tested
 * decision contract.
 */
export function decideInviteEntry(context: InviteEntryContext): InviteEntryDecision {
  const {
    kind,
    authenticated,
    availability,
    accountAccess,
    identityConflict = false
  } = context

  if (authenticated && accountAccess === 'disabled') {
    return { action: 'show_access_disabled' }
  }

  // Недействительная ссылка больше не участвует в авторизации и не пытается определить,
  // куда вести пользователя. Состояние ссылки не равно состоянию доступа: обычный вход
  // всегда выполняется отдельно через постоянный адрес приложения.
  if (availability !== 'available') {
    return {
      action: 'show_terminal',
      reason: availability
    }
  }

  if (!authenticated) {
    return {
      action: 'authenticate',
      mode: 'accept_invite',
      preserveInvite: true
    }
  }

  if (accountAccess === 'target_active') {
    return {
      action: 'open_account',
      destination: '/',
      reason: 'target_access_exists'
    }
  }

  if (identityConflict) {
    return {
      action: 'show_terminal',
      reason: 'identity_conflict'
    }
  }

  if (accountAccess === 'target_revoked') {
    return {
      action: 'show_terminal',
      reason: 'access_revoked'
    }
  }

  return { action: 'show_invite', kind }
}
